import { Link } from "react-router-dom";
import { ArrowRight, Moon, Sun, Menu, X, ChevronUp } from "lucide-react";
import { DemoRequestDialog } from "@/components/DemoRequestDialog";
import { RevenueGrowthHero } from "@/components/landing/RevenueGrowthHero";
import testimonialAvatarAsset from "@/assets/testimonial-avatar.jpg.asset.json";
import { useTheme } from "next-themes";
import { useEffect, useState, useCallback } from "react";
import { HeroMockup } from "@/components/landing/HeroMockup";
import { AnalyticsMockup, type AnalyticsTabKey } from "@/components/landing/AnalyticsMockup";
import { GmvPerformanceMockup } from "@/components/landing/GmvPerformanceMockup";
import { ReportsMockup } from "@/components/landing/ReportsMockup";
import { AddonProductsSection } from "@/components/landing/AddonProductsSection";
import { HostIntelligenceMockup } from "@/components/landing/HostIntelligenceMockup";
import { HostPortalMockup } from "@/components/landing/HostPortalMockup";

import { StackedLogo } from "@/components/StackedLogo";
import partnerShopee from "@/assets/partner-shopee.png";
import partnerTiktokShop from "@/assets/partner-tiktok-shop.png";
import partnerMeta from "@/assets/partner-meta.png";
import partnerTiktokBusiness from "@/assets/partner-tiktok-business.png";

/** Apply slate accent on mount */
const SLATE_HSL = "215 16% 47%";
const SLATE_DARK = "215 14% 55%";

const analyticsTabContent: Record<AnalyticsTabKey, { headline: string; description: string }> = {
  past: {
    headline: "Every session measured.\nEvery host scored.",
    description: "GMV, conversion rates, viewer engagement, and AI-powered host scores in a single dashboard. Compare sessions, track trends, and make data-driven decisions.",
  },
  brand: {
    headline: "Your brand performance\nat a glance.",
    description: "Weekly GMV trends, platform splits, and top performers, aggregated across all sessions. See which campaigns, hosts, and products drive the most revenue.",
  },
  host: {
    headline: "Know your best hosts.\nCoach the rest.",
    description: "AI-scored performance rankings based on GMV, conversion rate, viewer engagement, and session consistency. Spot trends and act before they become problems.",
  },
};

function AnalyticsSection() {
  const [activeTab, setActiveTab] = useState<AnalyticsTabKey>("past");
  const content = analyticsTabContent[activeTab];

  return (
    <section id="analytics" className="relative z-10 pt-16 md:pt-24 pb-16 md:pb-24 px-4 md:px-6 overflow-hidden">
      <div className="mx-auto max-w-[1200px]">
        <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">
          Live Intelligence
        </p>
        <h2
          key={activeTab + "-h"}
          className="text-[clamp(1.5rem,3vw,2.5rem)] font-[500] tracking-[-0.03em] text-foreground max-w-[540px] leading-[1.15] whitespace-pre-line transition-opacity duration-300"
        >
          {content.headline}
        </h2>
        <p
          key={activeTab + "-p"}
          className="mt-4 text-[14px] md:text-[15px] text-muted-foreground max-w-[440px] transition-opacity duration-300"
        >
          {content.description}
        </p>
        <div className="mt-8 md:mt-12">
          <div className="block lg:hidden -mx-4 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className="w-[980px] min-w-[980px] px-4">
              <AnalyticsMockup onTabChange={setActiveTab} />
            </div>
          </div>
          <div className="hidden lg:block">
            <AnalyticsMockup onTabChange={setActiveTab} />
          </div>
        </div>
      </div>
    </section>
  );
}

const Landing = () => {
  const { theme, setTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const sectionIds = ["hero", "analytics", "performance", "host-portal", "monitoring", "reports", "addons"];
    const onScroll = () => {
      const navHeight = 80;
      let current = "hero";
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= navHeight) {
          current = id;
        }
      }
      setActiveSection(current);
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isDark = theme === "dark";
  const diagonalLineColor = isDark ? "hsl(240 4% 26%" : "hsl(240 4% 80%";

  const navLinks = [
    { label: "Manager", href: "#hero" },
    { label: "Insights", href: "#analytics" },
    { label: "Revenue", href: "#performance" },
    { label: "Hosts", href: "#host-portal" },
    { label: "Monitoring", href: "#monitoring" },
    { label: "Reports", href: "#reports" },
    { label: "Add-on", href: "#addons" },
  ];

  const handleScrollTo = useCallback((href: string) => {
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      const navHeight = 56;
      const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
      window.scrollTo({ top, behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const isDark = theme === "dark";
    const hsl = isDark ? SLATE_DARK : SLATE_HSL;
    root.style.setProperty("--primary", hsl);
    root.style.setProperty("--ring", hsl);
    root.style.setProperty("--sidebar-primary", hsl);
    root.style.setProperty("--sidebar-ring", hsl);
  }, [theme]);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Controlled demo dialog (lifted here so it stays mounted when the mobile menu closes) */}
      <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} />

      {/* Nav */}
      <nav className="fixed top-0 z-50 w-full bg-background/95 backdrop-blur-sm border-b border-border px-4 md:px-6">
        <div className="mx-auto flex h-[56px] max-w-[1200px] items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center -ml-0.5 cursor-pointer shrink-0">
            <StackedLogo size={14} />
          </button>

          {/* Desktop nav links */}
          <div className="hidden xl:flex items-center gap-1 min-w-0 overflow-hidden">
            {navLinks.map((link) => {
              const isActive = activeSection === link.href.replace("#", "");
              return (
                <button
                  key={link.href}
                  onClick={() => handleScrollTo(link.href)}
                  className={`text-[12px] transition-colors h-8 px-2.5 whitespace-nowrap flex-shrink-0 ${isActive ? "text-foreground font-medium" : "text-foreground/60 hover:text-foreground"}`}
                >
                  {link.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="relative h-8 w-8 flex items-center justify-center text-foreground/70 hover:text-foreground transition-colors"
              title="Toggle theme"
            >
              <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            </button>
            <DemoRequestDialog>
              <button className="hidden xl:inline-flex items-center text-[13px] h-8 px-3 border border-foreground/40 text-foreground hover:bg-foreground hover:text-background transition-colors">
                Request demo
              </button>
            </DemoRequestDialog>
            {/* Mobile/tablet hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="xl:hidden h-8 w-8 flex items-center justify-center text-foreground/70 hover:text-foreground transition-colors"
            >
              {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

      </nav>

      {/* Mobile menu — full-screen overlay (outside nav to escape stacking context) */}
      {mobileMenuOpen && (
        <div className="xl:hidden fixed inset-0 top-[56px] z-[60] bg-background px-6 pt-8 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] flex flex-col overflow-y-auto">
          <div className="space-y-1 flex-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleScrollTo(link.href)}
                className="block w-full text-left text-[17px] font-medium text-foreground/70 hover:text-foreground transition-colors px-2 py-3"
              >
                {link.label}
              </button>
            ))}
          </div>
          <div className="pt-6 mt-auto border-t border-border shrink-0">
            <button
              type="button"
              onClick={() => {
                setMobileMenuOpen(false);
                setDemoOpen(true);
              }}
              className="w-full text-[14px] font-medium h-11 px-3 bg-foreground text-background hover:bg-foreground/90 transition-colors rounded-md"
            >
              Request demo
            </button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section id="hero" className="relative z-10 pt-16 pb-0 px-4 md:px-6 overflow-hidden">
        <div className="mx-auto max-w-[1200px] relative">
          <div className="pt-[40px] md:pt-[52px] pb-10 md:pb-16 relative flex flex-col md:flex-row">
            {/* Left column — text */}
            <div className="relative z-[3] flex-1 min-w-0 max-w-[540px]">
              <h1 className="text-[clamp(1.6rem,5vw,3.2rem)] font-[500] leading-[1.08] tracking-[-0.04em] text-foreground max-w-[540px]">
                Monitor and improve livestream performance at scale
              </h1>
              <p className="mt-4 md:mt-6 text-[14px] md:text-base leading-relaxed text-muted-foreground max-w-[420px]">
                Track host performance, compare sessions, manage schedules, and spot what impacts sales from one operational dashboard.
              </p>
              <div className="mt-8 md:mt-10 flex items-center gap-4">
                <DemoRequestDialog>
                  <button className="group relative inline-flex items-center gap-2 px-5 md:px-6 py-3 text-[14px] font-medium bg-foreground text-background transition-all duration-200 hover:bg-foreground/90">
                    Request a demo
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </button>
                </DemoRequestDialog>
              </div>
            </div>

            {/* Right column — Revenue growth animation */}
            <div className="hidden lg:block flex-1 relative z-[1]" style={{ minWidth: 0 }}>
              <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[500px] h-[380px]" style={{ transform: "translate(30px, -50%)" }}>
                <RevenueGrowthHero isDark={isDark} />
              </div>
            </div>
          </div>

          <div className="lg:block">
            <div className="block lg:hidden -mx-4 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <div className="w-[980px] min-w-[980px] px-4">
                <HeroMockup />
              </div>
            </div>
            <div className="hidden lg:block">
              <HeroMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Full-width divider */}
      <div className="relative z-10 w-full border-t border-border" />

      {/* Features — disabled */}

      {/* Full-width divider */}
      <div className="relative z-10 w-full border-t border-border" />

      {/* Analytics Section */}
      <AnalyticsSection />

      {/* Full-width divider */}
      <div className="relative z-10 w-full border-t border-border" />

      {/* GMV Performance Section */}
      <section id="performance" className="relative z-10 pt-16 md:pt-24 pb-16 md:pb-24 px-4 md:px-6 overflow-hidden bg-gradient-to-b from-accent/30 via-accent/10 to-transparent">
        <div className="mx-auto max-w-[1200px]">
          <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">
            Revenue & targets
          </p>
          <h2 className="text-[clamp(1.5rem,3vw,2.5rem)] font-[500] tracking-[-0.03em] text-foreground max-w-[540px] leading-[1.15]">
            Track host GMV.<br />Automate bonuses.
          </h2>
          <p className="mt-4 text-[14px] md:text-[15px] text-muted-foreground max-w-[440px]">
            Set revenue targets per host, define tiered bonus rules, and watch your leaderboard update in real time. Targets and bonuses update automatically as sessions run.
          </p>
          <div className="mt-8 md:mt-12">
            <div className="block lg:hidden -mx-4 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <div className="w-[980px] min-w-[980px] px-4">
                <GmvPerformanceMockup />
              </div>
            </div>
            <div className="hidden lg:block">
              <GmvPerformanceMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Full-width divider */}
      <div className="relative z-10 w-full border-t border-border" />

      {/* Host Portal Section */}
      <section id="host-portal" className="relative z-10 pt-16 md:pt-24 pb-16 md:pb-24 px-4 md:px-6 overflow-hidden">
        <div className="mx-auto max-w-[1200px]">
          <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">
            Host Portal
          </p>
          <h2 className="text-[clamp(1.5rem,3vw,2.5rem)] font-[500] tracking-[-0.03em] text-foreground max-w-[540px] leading-[1.15]">
            Give hosts their<br />own workspace.
          </h2>
          <p className="mt-4 text-[14px] md:text-[15px] text-muted-foreground max-w-[440px]">
            Hosts see their schedule, download session briefs, set availability, and track payouts through a self-service portal. Less coordination, fewer WhatsApp messages.
          </p>
          <div className="mt-8 md:mt-12">
            <div className="block lg:hidden -mx-4 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <div className="w-[980px] min-w-[980px] px-4">
                <HostPortalMockup />
              </div>
            </div>
            <div className="hidden lg:block">
              <HostPortalMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Full-width divider */}
      <div className="relative z-10 w-full border-t border-border" />

      {/* Host Intelligence Section */}
      <section id="monitoring" className="relative z-10 pt-16 md:pt-24 pb-16 md:pb-24 px-4 md:px-6 overflow-hidden">
        <div className="mx-auto max-w-[1200px]">
          <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">
            Real-time monitoring
          </p>
          <h2 className="text-[clamp(1.5rem,3vw,2.5rem)] font-[500] tracking-[-0.03em] text-foreground max-w-[540px] leading-[1.15]">
            Know what your hosts<br />are doing live.
          </h2>
          <p className="mt-4 text-[14px] md:text-[15px] text-muted-foreground max-w-[440px]">
            Monitor CTA frequency, speech activity, and inactivity risk across all live sessions. Get instant alerts when hosts need intervention before it costs you sales.
          </p>
          <div className="mt-8 md:mt-12">
            <div className="block lg:hidden -mx-4 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <div className="w-[980px] min-w-[980px] px-4">
                <HostIntelligenceMockup />
              </div>
            </div>
            <div className="hidden lg:block">
              <HostIntelligenceMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Full-width divider */}
      <div className="relative z-10 w-full border-t border-border" />

      {/* Reports Section */}
      <section id="reports" className="relative z-10 pt-16 md:pt-24 pb-16 md:pb-24 px-4 md:px-6 overflow-hidden">
        <div className="mx-auto max-w-[1200px]">
          <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">
            Reporting & insights
          </p>
          <h2 className="text-[clamp(1.5rem,3vw,2.5rem)] font-[500] tracking-[-0.03em] text-foreground max-w-[540px] leading-[1.15]">
            Generate reports<br />in one click.
          </h2>
          <p className="mt-4 text-[14px] md:text-[15px] text-muted-foreground max-w-[440px]">
            Create performance reports for management or branded campaign summaries for your clients. Pick a template, generate, and share reports with the latest data.
          </p>
          <div className="mt-8 md:mt-12">
            <ReportsMockup />
          </div>
        </div>
      </section>

      {/* Full-width divider */}
      <div className="relative z-10 w-full border-t border-border" />

      {/* Add-on Products */}
      <AddonProductsSection />

      {/* Testimonial — disabled */}

      {/* Full-width divider */}
      <div className="relative z-10 w-full border-t border-border" />

      {/* CTA */}
      <section className="relative z-10 pt-20 md:pt-32 pb-24 md:pb-40 px-4 md:px-6 overflow-hidden">
        <div className="mx-auto max-w-[1200px] text-center relative">
          <h2 className="text-[clamp(1.5rem,3.5vw,2.6rem)] font-[500] tracking-[-0.035em] text-foreground leading-[1.12] mx-auto max-w-[520px]">
            When you scale livestreams, you either get control or chaos.
          </h2>
          <p className="mt-5 text-[14px] md:text-[15px] leading-relaxed text-muted-foreground max-w-[420px] mx-auto">
            CAIRE helps your team monitor sessions, manage hosts, and improve performance without losing oversight.
          </p>
          <div className="mt-8 md:mt-10 flex justify-center">
            <DemoRequestDialog>
              <button
                className="group relative inline-flex items-center gap-2 px-5 md:px-6 py-3 text-[14px] font-medium transition-all duration-200 bg-foreground text-background hover:bg-foreground/90"
              >
                Request a demo
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </DemoRequestDialog>
          </div>
          <p className="mt-4 text-[13px] md:text-[14px] text-muted-foreground">
            Get started in days, and improve your next live session.
          </p>
        </div>
      </section>

      {/* Footer */}
      <div className="relative z-10 border-t border-border">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <StackedLogo size={16} />
          </div>
          <div className="flex flex-col md:flex-row items-center gap-3 md:gap-4">
            <div className="flex items-center gap-4">
              <Link to="/terms" className="text-[12px] text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
              <Link to="/privacy" className="text-[12px] text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
            </div>
            <div className="flex items-center gap-3">
              <a href="https://www.linkedin.com/company/meetcaire/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://www.instagram.com/meetcaire/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
            </div>
            <span className="text-[12px] text-muted-foreground text-center">© 2026 CAIRE.<br className="md:hidden" /> All rights reserved.</span>
          </div>
        </div>
      {/* Scroll to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`fixed bottom-6 right-6 z-50 h-10 w-10 flex items-center justify-center bg-foreground text-background rounded-full shadow-lg transition-all duration-300 hover:bg-foreground/90 ${showScrollTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
      >
        <ChevronUp className="h-5 w-5" />
      </button>
    </div>
    </div>
  );
};

export default Landing;
